// Preset trigger scenarios for the simulator — one per seeded workflow.
// Payload variable names MUST match the Thai templates seeded in the Go
// service (migrations/0002): templates render with missingkey=error, so a
// missing variable fails the delivery loudly (by design).
import type { TriggerRequest } from './api-noti'

export type Scenario = {
  id: string
  group: 'loan_card' | 'gov_pocket' | 'system'
  label: string
  description: string
  request: Omit<TriggerRequest, 'transactionId'>
}

export const SCENARIO_GROUPS: Record<Scenario['group'], string> = {
  loan_card: 'Loan Card (LMS/LOS)',
  gov_pocket: 'Gov Pocket (Klyro)',
  system: 'System',
}

export const scenarios: Scenario[] = [
  {
    id: 'loancard-approved',
    group: 'loan_card',
    label: 'อนุมัติสัญญาเงินกู้',
    description: 'แจ้งลูกค้า (push+email) และพนักงาน Maker (in-app) — โจทย์ LC ข้อ 3, 6.1.3-6.1.5',
    request: {
      name: 'loancard.contract.approved',
      to: [{ subscriberId: 'CIF-6900001' }, { subscriberId: 'EMP-5401879' }],
      payload: {
        customerName: 'หนึ่ง ในใจ',
        contractNo: 'VC-2026-000123',
        approvedAmount: 100000,
        confirmBy: '18 มิ.ย. 2569',
        cta: { deepLink: 'baacmobile://loancard/confirm/VC-2026-000123', label: 'ยืนยันวงเงิน' },
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'loancard-rejected',
    group: 'loan_card',
    label: 'ปฏิเสธสัญญาเงินกู้',
    description: 'แจ้งผลการปฏิเสธ — โจทย์ LC ข้อ 3',
    request: {
      name: 'loancard.contract.rejected',
      to: [{ subscriberId: 'CIF-6900002' }],
      payload: {
        customerName: 'สอง รัก',
        contractNo: 'VC-2026-000124',
        reason: 'คะแนน Credit Scoring ต่ำกว่าเกณฑ์ (ความเสี่ยงสูงมาก)',
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'loancard-contract-pdf',
    group: 'loan_card',
    label: 'ส่งสัญญาเงินกู้ (PDF)',
    description: 'อีเมลพร้อมไฟล์แนบสัญญา — โจทย์ LC 6.3.2',
    request: {
      name: 'loancard.contract.document',
      to: [{ subscriberId: 'CIF-6900001' }],
      payload: { customerName: 'หนึ่ง ในใจ', contractNo: 'VC-2026-000123' },
      overrides: {
        email: {
          attachments: [
            { name: 'สัญญาเงินกู้-VC-2026-000123.pdf', url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf' },
          ],
        },
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'loancard-credit-activated',
    group: 'loan_card',
    label: 'เปิดใช้วงเงินบัตร',
    description: 'โจทย์ LC ข้อ 3, 7.2',
    request: {
      name: 'loancard.credit.activated',
      to: [{ subscriberId: 'CIF-6900001' }],
      payload: { customerName: 'หนึ่ง ในใจ', cardLast4: '7731', creditLimit: 100000 },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'loancard-spend-qr',
    group: 'loan_card',
    label: 'ใช้จ่ายผ่าน QR',
    description: 'แจ้งทุกรายการสแกนจ่าย — โจทย์ LC 7.3',
    request: {
      name: 'loancard.card.spend.qr',
      to: [{ subscriberId: 'CIF-6900004' }],
      payload: {
        customerName: 'สี่ หมี่เกี๊ยว',
        amount: 50000,
        merchantName: 'ร้านวัสดุการเกษตรบางปลาม้า',
        balanceAfter: 30000,
        spentAt: '1 เม.ย. 2569 10:15 น.',
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'loancard-spend-transfer',
    group: 'loan_card',
    label: 'ใช้จ่ายผ่านโอนเงิน',
    description: 'โจทย์ LC ข้อ 3, 7.2',
    request: {
      name: 'loancard.card.spend.transfer',
      to: [{ subscriberId: 'CIF-6900004' }],
      payload: {
        customerName: 'สี่ หมี่เกี๊ยว',
        amount: 10000,
        toAccount: 'xxx-x-x5512-x',
        balanceAfter: 20000,
        spentAt: '1 เม.ย. 2569 14:00 น.',
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'loancard-suspended-attempt',
    group: 'loan_card',
    label: 'ใช้บัตรขณะถูกระงับ',
    description: 'แจ้งเหตุผิดปกติ — โจทย์ LC 7.2 (CIF 6900005)',
    request: {
      name: 'loancard.card.spend.suspended-attempt',
      to: [{ subscriberId: 'CIF-6900005' }],
      payload: {
        customerName: 'ห้า เสือ',
        amount: 10000,
        merchantName: 'ร้านเกษตรนิยม',
        attemptedAt: '2 พ.ค. 2569 09:40 น.',
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'loancard-refund',
    group: 'loan_card',
    label: 'คืนวงเงิน (ยกเลิกซื้อ)',
    description: 'โจทย์ LC ข้อ 3, 7.2',
    request: {
      name: 'loancard.card.refund.cancel-purchase',
      to: [{ subscriberId: 'CIF-6900004' }],
      payload: {
        customerName: 'สี่ หมี่เกี๊ยว',
        amount: 20000,
        merchantName: 'ร้านวัสดุการเกษตรบางปลาม้า',
        balanceAfter: 50000,
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'loancard-limit-decrease',
    group: 'loan_card',
    label: 'ปรับลดวงเงินเอง',
    description: 'โจทย์ LC ข้อ 3, 7.2 (CIF 6900007)',
    request: {
      name: 'loancard.card.limit.self-decrease',
      to: [{ subscriberId: 'CIF-6900007' }],
      payload: { customerName: 'เจ็ด ประจัญ', newLimit: 20000 },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'loancard-points',
    group: 'loan_card',
    label: 'สะสมแต้ม VC Point',
    description: 'Loyalty Earn/Burn — โจทย์ LC 7.1-7.3',
    request: {
      name: 'loancard.points.earn-burn',
      to: [{ subscriberId: 'CIF-6900004' }],
      payload: { action: 'สะสม', points: 500, pointsBalance: 500 },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'loancard-invoice-cycle',
    group: 'loan_card',
    label: 'ใบแจ้งหนี้รอบบัญชี',
    description: 'Batch ณ วันสรุปยอด (priority=bulk) — โจทย์ LC 8.1.1',
    request: {
      name: 'loancard.invoice.cycle',
      to: [{ subscriberId: 'CIF-6900001' }],
      payload: {
        customerName: 'หนึ่ง ในใจ',
        contractNo: 'VC-2026-000123',
        totalDue: 66000,
        dueDate: '30 มิ.ย. 2569',
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'loancard-invoice-overdue',
    group: 'loan_card',
    label: 'แจ้งยอดค้างชำระ',
    description: 'ติดตามหลังครบกำหนด — โจทย์ LC 8.1.2',
    request: {
      name: 'loancard.invoice.overdue',
      to: [{ subscriberId: 'CIF-6900002' }],
      payload: {
        customerName: 'สอง รัก',
        contractNo: 'VC-2026-000124',
        totalDue: 71700,
        overdueDays: 31,
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'govpocket-registered',
    group: 'gov_pocket',
    label: 'ได้รับสิทธิ์โครงการรัฐ',
    description: 'แจ้งผลลงทะเบียน + ชวนยืนยันสิทธิ์ — โจทย์ GP 2.4',
    request: {
      name: 'govpocket.registration.approved',
      to: [{ subscriberId: 'CIF-0012345678' }],
      payload: {
        projectName: 'โครงการไร่ละพันร่วมจ่าย (80:20)',
        entitlementAmount: 20000,
        confirmBy: '31 ธ.ค. 2569',
        cta: { deepLink: 'baacmobile://govpocket/confirm', label: 'ยืนยันสิทธิ์' },
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'govpocket-activated',
    group: 'gov_pocket',
    label: 'ยืนยันสิทธิ์สำเร็จ',
    description: 'สิทธิ์พร้อมใช้ + เตือนเติมเงินสมทบ — โจทย์ GP 2.5-2.6',
    request: {
      name: 'govpocket.entitlement.activated',
      to: [{ subscriberId: 'CIF-0012345678' }],
      payload: { projectName: 'โครงการไร่ละพันร่วมจ่าย (80:20)', balance: 16000 },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'govpocket-spend',
    group: 'gov_pocket',
    label: 'ใช้จ่ายกระเป๋ารัฐ (ลูกค้า+ร้านค้า)',
    description: 'Real-time สองผู้รับในนัดเดียว — โจทย์ GP 3.2-3.3 (เกณฑ์คะแนน Real Time)',
    request: {
      name: 'govpocket.spend.completed',
      to: [{ subscriberId: 'CIF-0012345678' }, { subscriberId: 'MERCH-88021' }],
      payload: {
        amount: 10000,
        govAmount: 8000,
        customerAmount: 2000,
        merchantName: 'ร้านปุ๋ยรุ่งเรืองการเกษตร',
        txnRef: 'TXN20260514-000123',
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'govpocket-clawback',
    group: 'gov_pocket',
    label: 'เรียกคืนวงเงินโครงการ',
    description: 'แจ้ง + รอลูกค้ายืนยัน (critical) — โจทย์ GP 4.3',
    request: {
      name: 'govpocket.clawback.requested',
      to: [{ subscriberId: 'CIF-0012345678' }],
      payload: {
        projectName: 'โครงการไร่ละพันร่วมจ่าย (80:20)',
        clawbackAmount: 10000,
        reason: 'พื้นที่เพาะปลูกจริง 10 ไร่ จากที่แจ้ง 20 ไร่',
        deadline: '30 มิ.ย. 2569',
        cta: { deepLink: 'baacmobile://govpocket/clawback/confirm', label: 'ยืนยันการเปลี่ยนแปลงสิทธิ์' },
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'system-broadcast',
    group: 'system',
    label: 'Broadcast ผ่าน Topic',
    description: 'ประกาศถึงสมาชิก topic ทั้งหมด (fan-out) — ตั้ง topic ผ่าน API ก่อน',
    request: {
      name: 'system.broadcast.maintenance',
      to: [{ type: 'topic', topicKey: 'employees-all' }],
      payload: {
        message: 'ระบบ Virtual Card จะปิดปรับปรุงเพื่ออัพเดตเวอร์ชันใหม่',
        windowStart: '14 มิ.ย. 2569 22:00 น.',
        windowEnd: '15 มิ.ย. 2569 02:00 น.',
      },
      actor: 'portal-simulator',
    },
  },
  {
    id: 'system-mock',
    group: 'system',
    label: 'Smoke test (mock)',
    description: 'ทดสอบ pipeline โดยไม่ส่งออกจริง',
    request: {
      name: 'system.mock.smoke',
      to: [{ subscriberId: 'CIF-6900001' }],
      payload: { note: 'ทดสอบจาก portal simulator' },
      actor: 'portal-simulator',
    },
  },
]
